<template>
    <div class="flex flex-column w-full" style="color: var(--theme-font-color);">
        <div class="w-full flex flex-row align-items-center">
            <div class="subtitle p-0 m-0 w-full justify-self-start flex flex-row align-items-center justify-content-start gap-2">
                <span>Authorized Users</span>
                <Button v-if="user.id === license.admin.id" class="m-0 p-0" rounded text @click="showAddNewUserDialog = true" style="height: 100%; border-radius: 980px;">
                    <SvgPlusCircle className="pluscircle-svg" color="var(--button-normal-color)"/>
                </Button>
            </div>
        </div>
        
        <div class="mt-2" style="border: 1px solid var(--table-border-color); width: 100%; border-radius: var(--my-border-radius);">
            <ScrollPanel style="min-height: 0px; width: 100%;" :style="`max-height: ${windowWidth>575 ? '18' : '10'}rem; height: ${windowWidth>575 ? '18' : '10'}rem;`" class="px-1">
                <div style="padding: 0px; padding-bottom: 1.2rem; width: 100%;">
                    <DataView v-if="user.id === license.admin.id" :value="delegatesListSorted" class="w-full px-1 my-1">
                        <template #empty>
                            <div class="w-full flex flex-row justify-content-start align-items-center py-1 pr-3" style="background-color: var(--table-row-color);">
                                <span>You haven't added any users yet. Get started by using the button above or do it through the <span style="font-weight: var(--font-weight-bold);">Users</span> panel.</span>
                            </div>
                        </template>
                        <template #list="slotProps">
                            <div class="grid grid-nogutter">
                                <div v-for="(item, index) in slotProps.items" :key="index" class="px-0 flex flex-row align-items-center w-full" :class="{'is-added-user': (license.delegates.filter((user) => user.id === item.id).length===0) && delegatesModified.filter((user) => user.id === item.id).length>0, 'is-removed-user': (license.delegates.filter((user) => user.id === item.id).length>0) && delegatesModified.filter((user) => user.id === item.id).length===0}" style="padding: 0.3rem 0.5rem; font-size: small; color: var(--theme-font-color); margin-right: 1.2rem;" @mouseover="authorizedUsersHoverIndex = index" @mouseleave="authorizedUsersHoverIndex = null">
                                    <div class="px-0 delegates-item flex flex-row align-items-center gap-2 w-full">
                                        <Checkbox v-if="license.current_status.activated && license.current_status.activeUser && license.current_status.activeUser.id === item.id" v-model="delegatesModified" :value="item" disabled v-tooltip.top="{ value: 'Please stop the running activation if you really want to remove their authorization.' }">
                                            <template #icon="scope">
                                                <SvgLock v-if="scope.checked" className="checkbox-icon-svg" color="var(--checkbox-icon-color)"/>
                                            </template>
                                        </Checkbox>
                                        <Checkbox v-else v-model="delegatesModified" :value="item"/>
                                        <span class="subtext">{{item.first_name}} {{item.last_name}} ({{item.email}}){{ (item.id===user.id) ? ' - You' : '' }}</span>
                                    </div>
                                    <div v-tooltip.top="{ value: 'Transfer ownership.', showDelay: 500 }">
                                        <SvgUpgrade v-if="authorizedUsersHoverIndex!=null && authorizedUsersHoverIndex==index" className="upgrade-svg" color="var(--button-normal-color)" @click="clickedTransferOwnership(item)" style="cursor: pointer;"/>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DataView>
                    <DataView v-else :value="delegatesListSorted" style="padding: 0px; padding-bottom: 1.2rem; width: 100%;" class="w-full">
                        <template #empty>
                            <div class="w-full flex flex-row justify-content-start align-items-center py-1" style="background-color: var(--table-row-color); font-size: small;">
                                <span>There is no authorized users assigned to this license</span>
                            </div>
                        </template>
                        <template #list="slotProps">
                            <div class="grid grid-nogutter">
                                <div v-for="(item, index) in slotProps.items" :key="index" class="delegates-item flex flex-row align-items-center justify-content-start gap-2 w-full" :class="{'is-added-user': (license.delegates.filter((user) => user.id === item.id).length===0) && delegatesModified.filter((user) => user.id === item.id).length>0, 'is-removed-user': (license.delegates.filter((user) => user.id === item.id).length>0) && delegatesModified.filter((user) => user.id === item.id).length===0}" style="padding: 0.3rem 0.5rem; font-size: small; color: var(--theme-font-color);">
                                    <span class="subtext">{{item.first_name}} {{item.last_name}} ({{item.email}}){{ (item.id===user.id) ? ' - You' : '' }}</span>
                                </div>
                            </div>
                        </template>
                    </DataView>
                </div>
            </ScrollPanel>
        </div>

        <!-- DIALOGS -->

        <!-- Add a New User window -->
        <AddNewUserDialog :showDialog="showAddNewUserDialog" :linkedLicenses="[license]" @close="showAddNewUserDialog = false" @added-new-user="(userInfo) => delegatesModified = [userInfo, ...delegatesModified]"/>

        <!-- Transfer Ownership -->
        <ConfirmationTransferOwnershipDialog :showDialog="showConfirmationTransferOwnershipDialog" :license="license" :newOwner="userNewOwner" @close="showConfirmationTransferOwnershipDialog = false" @close-license-window="emit('close-license-window')" @refresh-licenses-table="emit('refresh-licenses-table')" @refresh-license="emit('refresh-license')"/>
    </div>

</template>

<script setup>
import { ref, toRefs, computed, watchEffect, onMounted, onBeforeUnmount } from 'vue';
import ScrollPanel from 'primevue/scrollpanel'
import DataView from 'primevue/dataview';
import Checkbox from 'primevue/checkbox';
// PrimeVue
import Button from 'primevue/button'
// vue
import AddNewUserDialog from '@/components/Dialogs/Actions/AddNewUserDialog.vue'
import SvgPlusCircle from '@/components/SVGs/SvgPlusCircle.vue';
import SvgLock from '@/components/SVGs/SvgLock.vue';
import SvgUpgrade from '@/components/SVGs/SvgUpgrade.vue';
import ConfirmationTransferOwnershipDialog from '@/components/Dialogs/Actions/ConfirmationTransferOwnershipDialog.vue';
// Others
import { useStore } from 'vuex';
import UsersUtils from '@/composables/UsersUtils';

const store = useStore()
const { allUsers } = UsersUtils(store)

// --- PROPS ---
const props = defineProps({
    license: Object,
})
const { license } = toRefs(props)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- EMITS ---
const emit = defineEmits(['delegates-change-click'])

// --- USERS ---
const delegatesListSorted = computed(() => {
    if (user.value.id === license.value.admin.id) { // I am the admin => I show all my users
        return allUsers.value.sort((a,b) => { // SORT IN THE ALPHABETICAL ORDER
            const nameA = a.first_name.toLowerCase() + a.last_name.toLowerCase()
            const nameB = b.first_name.toLowerCase() + b.last_name.toLowerCase()
            if (nameA < nameB) { return -1 }
            else if (nameA > nameB) { return 1 }
            else { return 0 }
        })
    }
    else { // I am one of the delegates => We only see the delegates list
        return license.value.delegates
    }
})

// - Add a New User -
const showAddNewUserDialog = ref(false)

// - CHANGING/RESET -
const delegatesModified = ref(license.value.delegates) // Copy, used for a reset for example
// When the license delegates is updated from the LicenseWindowDialog, we should reset the delegatesModified to the one the license has at this moment
watchEffect(() => {
    if (license.value.delegates) {
        delegatesModified.value = license.value.delegates
    }
})
// When the delegatesModified changes, if it is different than the list of delegates of the license, we should trigger a function that will make the HTTP request (make an emit)
watchEffect(() => {
    if (delegatesModified.value != license.value.delegates) {
        emit('delegates-change-click', delegatesModified.value)
        delegatesModified.value = license.value.delegates // This ensures that then the window will show the updated value (either the ones with the modified list, or the same as just before the modification trial if there were an error)
    }
})

// -- TRANSFER OWNERSHIP --
const authorizedUsersHoverIndex = ref(null);
const userNewOwner = ref(null)
const showConfirmationTransferOwnershipDialog = ref(false)
const clickedTransferOwnership = (user) => {
    userNewOwner.value = user
    showConfirmationTransferOwnershipDialog.value = true
}

// -- WINDOW WIDTH TRACKING (for responsiveness of ui) --
const windowWidth = ref(window.innerWidth);
const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};
onMounted(() => {
  window.addEventListener('resize', updateWindowWidth);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth);
});
</script>

<style scoped>
.detail {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    margin-top: 1rem;
}
.detail .detail-label {
    font-weight: bolder;
}
.detail .detail-value {
    font-weight: lighter;
}
:deep(.p-scrollpanel-content) {
    padding: 0;
}
.scrollpanel-footer-button:hover {
    background-color: var(--surface-200)
}
:deep(div.p-overlaypanel-content) {
    padding: 0rem !important;
}
:deep(.p-button) {
    border: 0px solid black !important;
}
:deep(.p-disabled, .p-component:disabled) {
    opacity: 1 !important;
}
</style>